/**
 * Tids-/lysvindu-hjelpere. ÉN sannhet for «hvor lenge lyser lampa», delt av
 * SolBue, PotteKort, AnleggPanel og LysSheet (som før hadde hver sin kopi —
 * og to av dem var uenige om on===off-kanten).
 *
 * Speiler firmwaren (`logic.light_should_be_on`): on===off betyr «ingen lystid»,
 * altså 0 — IKKE 24 t. Da viser appen samme varighet/DLI som potta faktisk kjører.
 */

/** "07:30" → 450 minutter siden midnatt. Ugyldig/manglende → 0. */
export function hhmmTilMin(s: string | null | undefined): number {
  if (!s) return 0;
  const [h, m] = s.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return (h ?? 0) * 60 + (m ?? 0);
}

/**
 * Lengden på lys-vinduet i minutter (0–1440). Håndterer at vinduet krysser
 * midnatt (off < on). on===off → 0 (samme som firmwaren: lyset er av).
 */
export function lysVarighetMin(on: string | null | undefined, off: string | null | undefined): number {
  if (!on || !off) return 0; // halv-redigert/tomt felt → ingen lystid (som firmwaren)
  const o = hhmmTilMin(on);
  const f = hhmmTilMin(off);
  if (o === f) return 0;
  return (((f - o) % 1440) + 1440) % 1440;
}

/** Samme som lysVarighetMin, men i timer (kan være desimal). */
export function lysVarighetTimer(on: string | null | undefined, off: string | null | undefined): number {
  return lysVarighetMin(on, off) / 60;
}

/**
 * Minutter siden midnatt i NORSK tid (Europe/Oslo, sommertid automatisk).
 *
 * Lysplanen (timer_on/timer_off) er norsk tid — firmwaren regner CET/CEST selv.
 * Brukte appen telefonens lokale klokke, ville «Lyser nå» og sola i SolBue
 * bomme med tidsforskjellen straks telefonen står i en annen tidssone (ferie,
 * feil innstilling). Faller tilbake på lokal tid hvis Intl mangler sonen.
 */
const osloKlokke = (() => {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Oslo',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
  } catch {
    return null;
  }
})();

export function norskMinuttAvDognet(d: Date): number {
  if (osloKlokke) {
    const deler = osloKlokke.formatToParts(d);
    const h = Number(deler.find((p) => p.type === 'hour')?.value);
    const m = Number(deler.find((p) => p.type === 'minute')?.value);
    if (Number.isFinite(h) && Number.isFinite(m)) return (h % 24) * 60 + m;
  }
  return d.getHours() * 60 + d.getMinutes();
}

/**
 * Hvor langt inn i lysvinduet vi er, i minutter (0–1439), regnet fra timer_on.
 * Håndterer vindu over midnatt: 23:30 med on=22:00 → 90, 01:00 → 180.
 */
export function minutterInnILysvindu(on: string | null | undefined, naa: Date): number {
  return (((norskMinuttAvDognet(naa) - hhmmTilMin(on)) % 1440) + 1440) % 1440;
}

/**
 * Står lyset på akkurat nå etter planen? Samme kant som firmwaren
 * (`on <= nå < off`): i selve av-minuttet er lyset slukket, og on===off = av.
 */
export function iLysvindu(on: string | null | undefined, off: string | null | undefined, naa: Date): boolean {
  const varighet = lysVarighetMin(on, off);
  return varighet > 0 && minutterInnILysvindu(on, naa) < varighet;
}
